import React, { useState, useEffect } from "react";
import LZString from "lz-string";

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [progress, setProgress] = useState(0); // Progress for the progress bar
    const [progressInfo, setProgressInfo] = useState({ current: 0, total: 0, eta: null, currentBatch: "" });
    const [cancelRequested, setCancelRequested] = useState(false);
    const [completionInfo, setCompletionInfo] = useState(null);

    const fetchOrdersBatch = async (startDate, endDate, retries = 3) => {
        try {
            const url = `https://bymolive-backend-995926394015.us-central1.run.app/api/orders-for-month?start_date=${startDate}&end_date=${endDate}`;
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Failed to fetch orders: ${response.statusText}`);
            }

            const data = await response.json();
            console.log(`Fetched orders for ${startDate} to ${endDate}:`, data);

            return data.orders; // Return raw orders
        } catch (err) {
            console.error(`Error fetching orders for ${startDate} to ${endDate}:`, err.message);
            
            // Retry logic with exponential backoff
            if (retries > 0) {
                console.log(`Retrying... ${retries} attempts left`);
                await new Promise(resolve => setTimeout(resolve, 2000)); // 2-second delay before retry
                return fetchOrdersBatch(startDate, endDate, retries - 1);
            }
            
            setError(`Failed to fetch orders for ${startDate} to ${endDate}. Please try again.`);
            return [];
        }
    };


    const fetchOrdersInRange = async (startDate, endDate, storageKey) => {
        console.log(`Fetching orders from ${startDate} to ${endDate} in 8-day batches...`);
        setLoading(true);
        setError("");
        setProgress(0);
        setCancelRequested(false);
        setCompletionInfo(null);
        
        const startTime = Date.now();

        let currentEnd = new Date(endDate); // Start from today (or the provided end date)
        const targetStart = new Date(startDate); // Target start date (01.06.2024)
        let allRawOrders = []; // Store raw orders
        let allOrders = []; // Store reduced orders
        const totalDays = Math.ceil((currentEnd - targetStart) / (1000 * 60 * 60 * 24));
        const totalBatches = Math.ceil(totalDays / 8); // Total 8-day batches

        setProgressInfo({ current: 0, total: totalBatches, eta: null, currentBatch: "" });

        let batchCount = 0;

        while (currentEnd >= targetStart && !cancelRequested) {
            const batchStart = new Date(currentEnd);
            batchStart.setDate(currentEnd.getDate() - 7); // Subtract 7 days to create an 8-day range

            const batchStartDate = batchStart >= targetStart ? batchStart.toISOString().split("T")[0] : startDate;
            const batchEndDate = currentEnd.toISOString().split("T")[0];

            // Update current batch info
            setProgressInfo(prev => ({
                ...prev,
                currentBatch: `${batchStartDate} to ${batchEndDate}`
            }));

            // Add 1-second delay between API calls to respect Shopify's 1/s rate limit
            if (batchCount > 0) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            // Check for cancellation before making the API call
            if (cancelRequested) {
                console.log("Operation cancelled by user");
                break;
            }

            const batchOrders = await fetchOrdersBatch(batchStartDate, batchEndDate);
            allRawOrders = [...allRawOrders, ...batchOrders];

            // Reduce orders for UI display
            const reducedBatchOrders = batchOrders.map(order => ({
                id: order.id,
                created_at: order.created_at,
                total_price: order.total_price,
                discount_code: order.discount_codes?.[0]?.code || null,
                discount_amount: order.discount_codes?.[0]?.amount || null,
            }));
            allOrders = [...allOrders, ...reducedBatchOrders];

            // Update progress
            batchCount += 1;
            const currentProgress = ((batchCount / totalBatches) * 100).toFixed(2); // Format to 2 decimal places
            setProgress(currentProgress);

            // Calculate ETA
            const elapsed = Date.now() - startTime;
            const avgTimePerBatch = elapsed / batchCount;
            const remainingBatches = totalBatches - batchCount;
            const etaMs = remainingBatches > 0 ? (avgTimePerBatch * remainingBatches) : 0;

            setProgressInfo(prev => ({
                ...prev,
                current: batchCount,
                eta: etaMs
            }));

            // Move to the previous batch
            currentEnd.setDate(currentEnd.getDate() - 8);
        }

        // Only save data if not cancelled and we have data
        if (!cancelRequested && allOrders.length > 0) {
            // Store unique orders for display
            const uniqueOrders = Array.from(
                new Map(allOrders.map((order) => [order.id, order])).values()
            ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setOrders(uniqueOrders);

            // Update data info
            if (uniqueOrders.length > 0) {
                const dates = uniqueOrders.map(order => new Date(order.created_at)).sort((a, b) => a - b);
                const oldestDate = dates[0].toLocaleDateString();
                const newestDate = dates[dates.length - 1].toLocaleDateString();
                setDataInfo({
                    hasData: true,
                    orderCount: uniqueOrders.length,
                    dateRange: `${oldestDate} - ${newestDate}`
                });
            }

            // Compress and save both raw and reduced orders in session storage
            const compressedReducedData = LZString.compress(JSON.stringify(uniqueOrders));
            const compressedRawData = LZString.compress(JSON.stringify(allRawOrders));
            sessionStorage.setItem(storageKey, compressedReducedData); // Save reduced orders
            sessionStorage.setItem("rawOrdersData", compressedRawData); // Save raw orders

            // Calculate completion info
            const endTime = Date.now();
            const totalTime = (endTime - startTime) / 1000; // Convert milliseconds to seconds
            setCompletionInfo({
                totalOrders: uniqueOrders.length,
                totalTime: totalTime.toFixed(2) + " seconds"
            });
        }

        if (cancelRequested) {
            setError("Operation cancelled by user");
        }

        setLoading(false);
        setCancelRequested(false);
        setProgressInfo({ current: 0, total: 0, eta: null, currentBatch: "" });
    };

    // New function for incremental update - fetch last 45 days and merge with existing data
    const fetchRecentOrdersAndMerge = async () => {
        console.log("Fetching recent orders (last 45 days) and merging with existing data...");
        setLoading(true);
        setError("");
        setProgress(0);
        setCancelRequested(false);
        setCompletionInfo(null);
        
        const startTime = Date.now();

        // Calculate date range for last 45 days
        const today = new Date();
        const fortyFiveDaysAgo = new Date();
        fortyFiveDaysAgo.setDate(today.getDate() - 45);
        
        const startDate = fortyFiveDaysAgo.toISOString().split("T")[0];
        const endDate = today.toISOString().split("T")[0];

        // Load existing data
        let existingRawOrders = [];
        let existingReducedOrders = [];
        
        try {
            const compressedRawData = sessionStorage.getItem("rawOrdersData");
            const compressedReducedData = sessionStorage.getItem("ordersData");
            
            if (compressedRawData) {
                existingRawOrders = JSON.parse(LZString.decompress(compressedRawData)) || [];
                console.log(`Found ${existingRawOrders.length} existing raw orders`);
            }
            
            if (compressedReducedData) {
                existingReducedOrders = JSON.parse(LZString.decompress(compressedReducedData)) || [];
                console.log(`Found ${existingReducedOrders.length} existing reduced orders`);
            }
        } catch (err) {
            console.error("Error loading existing data:", err);
        }

        // Fetch recent orders in batches
        let currentEnd = new Date(endDate);
        const targetStart = new Date(startDate);
        let newRawOrders = [];
        let newReducedOrders = [];
        const totalDays = Math.ceil((currentEnd - targetStart) / (1000 * 60 * 60 * 24));
        const totalBatches = Math.ceil(totalDays / 8);

        setProgressInfo({ current: 0, total: totalBatches, eta: null, currentBatch: "" });

        let batchCount = 0;

        while (currentEnd >= targetStart && !cancelRequested) {
            const batchStart = new Date(currentEnd);
            batchStart.setDate(currentEnd.getDate() - 7);

            const batchStartDate = batchStart >= targetStart ? batchStart.toISOString().split("T")[0] : startDate;
            const batchEndDate = currentEnd.toISOString().split("T")[0];

            setProgressInfo(prev => ({
                ...prev,
                currentBatch: `${batchStartDate} to ${batchEndDate}`
            }));

            if (batchCount > 0) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            if (cancelRequested) {
                console.log("Operation cancelled by user");
                break;
            }

            const batchOrders = await fetchOrdersBatch(batchStartDate, batchEndDate);
            newRawOrders = [...newRawOrders, ...batchOrders];

            const reducedBatchOrders = batchOrders.map(order => ({
                id: order.id,
                created_at: order.created_at,
                total_price: order.total_price,
                discount_code: order.discount_codes?.[0]?.code || null,
                discount_amount: order.discount_codes?.[0]?.amount || null,
            }));
            newReducedOrders = [...newReducedOrders, ...reducedBatchOrders];

            batchCount += 1;
            const currentProgress = ((batchCount / totalBatches) * 100).toFixed(2);
            setProgress(currentProgress);

            const elapsed = Date.now() - startTime;
            const avgTimePerBatch = elapsed / batchCount;
            const remainingBatches = totalBatches - batchCount;
            const etaMs = remainingBatches > 0 ? (avgTimePerBatch * remainingBatches) : 0;

            setProgressInfo(prev => ({
                ...prev,
                current: batchCount,
                eta: etaMs
            }));

            currentEnd.setDate(currentEnd.getDate() - 8);
        }

        if (!cancelRequested && newRawOrders.length >= 0) {
            // Remove orders from the last 45 days from existing data
            const cutoffDate = fortyFiveDaysAgo.getTime();
            
            const filteredExistingRaw = existingRawOrders.filter(order => {
                const orderDate = new Date(order.created_at).getTime();
                return orderDate < cutoffDate;
            });
            
            const filteredExistingReduced = existingReducedOrders.filter(order => {
                const orderDate = new Date(order.created_at).getTime();
                return orderDate < cutoffDate;
            });

            console.log(`Removed ${existingRawOrders.length - filteredExistingRaw.length} orders from last 45 days`);
            console.log(`Adding ${newRawOrders.length} new orders`);

            // Merge old data (>45 days) with new data (last 45 days)
            const mergedRawOrders = [...filteredExistingRaw, ...newRawOrders];
            const mergedReducedOrders = [...filteredExistingReduced, ...newReducedOrders];

            // Remove duplicates and sort
            const uniqueRawOrders = Array.from(
                new Map(mergedRawOrders.map(order => [order.id, order])).values()
            ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            const uniqueReducedOrders = Array.from(
                new Map(mergedReducedOrders.map(order => [order.id, order])).values()
            ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            setOrders(uniqueReducedOrders);

            // Update data info
            if (uniqueReducedOrders.length > 0) {
                const dates = uniqueReducedOrders.map(order => new Date(order.created_at)).sort((a, b) => a - b);
                const oldestDate = dates[0].toLocaleDateString();
                const newestDate = dates[dates.length - 1].toLocaleDateString();
                setDataInfo({
                    hasData: true,
                    orderCount: uniqueReducedOrders.length,
                    dateRange: `${oldestDate} - ${newestDate}`
                });
            }

            // Save merged data
            const compressedReducedData = LZString.compress(JSON.stringify(uniqueReducedOrders));
            const compressedRawData = LZString.compress(JSON.stringify(uniqueRawOrders));
            sessionStorage.setItem("ordersData", compressedReducedData);
            sessionStorage.setItem("rawOrdersData", compressedRawData);

            const endTime = Date.now();
            const totalTime = (endTime - startTime) / 1000;
            setCompletionInfo({
                totalOrders: uniqueReducedOrders.length,
                totalTime: totalTime.toFixed(2) + " seconds",
                newOrdersAdded: newRawOrders.length,
                oldOrdersKept: filteredExistingRaw.length
            });

            console.log(`Update complete: ${uniqueReducedOrders.length} total orders (${newRawOrders.length} new, ${filteredExistingRaw.length} existing)`);
        }

        if (cancelRequested) {
            setError("Operation cancelled by user");
        }

        setLoading(false);
        setCancelRequested(false);
        setProgressInfo({ current: 0, total: 0, eta: null, currentBatch: "" });
    };

    const handleGetAllData = () => {
        const startDate = "2024-06-01";
        const today = new Date();
        const endDate = today.toISOString().split("T")[0];

        fetchOrdersInRange(startDate, endDate, "ordersData");
    };

    const handleUpdateRecentData = () => {
        fetchRecentOrdersAndMerge();
    };

    const handleCancelOperation = () => {
        setCancelRequested(true);
    };







    const [dataInfo, setDataInfo] = useState({ hasData: false, orderCount: 0, dateRange: null });

    useEffect(() => {
        const compressedData = sessionStorage.getItem("ordersData");
        if (compressedData) {
            const decompressedData = JSON.parse(LZString.decompress(compressedData));
            const ordersData = decompressedData || [];
            setOrders(ordersData);
            
            // Calculate data info
            if (ordersData.length > 0) {
                const dates = ordersData.map(order => new Date(order.created_at)).sort((a, b) => a - b);
                const oldestDate = dates[0].toLocaleDateString();
                const newestDate = dates[dates.length - 1].toLocaleDateString();
                setDataInfo({
                    hasData: true,
                    orderCount: ordersData.length,
                    dateRange: `${oldestDate} - ${newestDate}`
                });
            }
        }
    }, []);

    return (
        <div style={{ padding: "20px" }}>
            <h1>Shopify Orders</h1>
            
            {dataInfo.hasData && (
                <div style={{ 
                    marginBottom: "20px", 
                    padding: "10px", 
                    backgroundColor: "#e9ecef", 
                    borderRadius: "5px",
                    fontSize: "14px"
                }}>
                    <strong>Current Data:</strong> {dataInfo.orderCount} orders ({dataInfo.dateRange})
                </div>
            )}
            {!loading ? (
                <>
                    <button
                        onClick={handleGetAllData}
                        style={{
                            padding: "10px 20px",
                            marginRight: "10px",
                            backgroundColor: "#007bff",
                            color: "#fff",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                    >
                        Get All Data
                    </button>

                    <button
                        onClick={handleUpdateRecentData}
                        style={{
                            padding: "10px 20px",
                            marginRight: "10px",
                            backgroundColor: "#28a745",
                            color: "#fff",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                    >
                        Update Recent Data (Last 45 Days)
                    </button>
                </>
            ) : (
                <button
                    onClick={handleCancelOperation}
                    style={{
                        padding: "10px 20px",
                        marginRight: "10px",
                        backgroundColor: "#dc3545",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                    }}
                >
                    Cancel Operation
                </button>
            )}

            {loading && (
                <>
                    <p>Loading orders...</p>
                    <div style={{ margin: "10px 0" }}>
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                            <span style={{ marginRight: "10px" }}>{progress}%</span>
                            <div style={{ flex: 1, backgroundColor: "#ddd", borderRadius: "4px", overflow: "hidden" }}>
                                <div style={{
                                    width: `${progress}%`,
                                    backgroundColor: "#4CAF50",
                                    height: "8px",
                                    transition: "width 2s ease-out"
                                }} />
                            </div>
                        </div>
                        <div style={{ fontSize: "14px", color: "#666" }}>
                            <div>Batch {progressInfo.current} of {progressInfo.total}</div>
                            {progressInfo.currentBatch && (
                                <div>Current: {progressInfo.currentBatch}</div>
                            )}
                            {progressInfo.eta > 0 && (
                                <div>ETA: {Math.floor(progressInfo.eta / 60000)}m {Math.floor((progressInfo.eta % 60000) / 1000)}s remaining</div>
                            )}
                        </div>
                    </div>
                </>
            )}
            {error && <p style={{ color: "red" }}>{error}</p>}
            {orders.length > 0 ? (
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        marginTop: "20px",
                    }}
                >
                    <thead>
                    <tr>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>Order ID</th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>Date</th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>Discount Code</th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>Discount Amount</th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>Total Price</th>
                    </tr>
                    </thead>
                    <tbody>
                    {orders.map((order) => (
                        <tr key={order.id}>
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>{order.id}</td>
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {new Date(order.created_at).toLocaleString()}
                            </td>
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order.discount_code || "N/A"}
                            </td>
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order.discount_amount || "N/A"}
                            </td>
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order.total_price}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            ) : (
                !loading && <p>No orders found.</p>
            )}

            {completionInfo && (
                <div style={{ marginTop: "20px", padding: "10px", border: "1px solid #ddd", borderRadius: "5px" }}>
                    <h3>Operation Completion Summary</h3>
                    <p>Total Orders: {completionInfo.totalOrders}</p>
                    <p>Total Time: {completionInfo.totalTime}</p>
                    {completionInfo.newOrdersAdded && (
                        <p>New Orders Added: {completionInfo.newOrdersAdded}</p>
                    )}
                    {completionInfo.oldOrdersKept && (
                        <p>Old Orders Kept: {completionInfo.oldOrdersKept}</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default Orders;

